import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, HeartPulse, TrendingUp } from "lucide-react";
import Logo from "../components/Logo";
import WaitlistStrip from "../components/WaitlistStrip";

/**
 * Main opening page — audience selection.
 * Presents two clearly separated portals: ANA concept preview and Private Investor Preview.
 * Retains the cosmic MeditationSceneBackground and central My Mood Groove identity.
 */
export default function PortalChoice() {
  return (
    <div
      className="relative min-h-screen w-full flex items-center justify-center px-6 py-14 overflow-hidden"
      data-testid="portal-choice-page"
    >
      {/* Atmospheric video — first impression at mymoodgroove.com */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <video
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          data-testid="portal-choice-video"
        >
          <source src="/investor/atmospheric-loop.webm" type="video/webm" />
          <source src="/investor/atmospheric-loop.mp4" type="video/mp4" />
        </video>
        {/* Radial vignette so the two portal cards read cleanly in the center */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at center, rgba(3,6,15,0.25) 0%, rgba(3,6,15,0.60) 55%, rgba(3,6,15,0.88) 100%)",
          }}
        />
      </div>

      <div className="relative z-10 w-full max-w-5xl flex flex-col items-center text-center">
        {/* Wordmark */}
        <div className="mb-4">
          <Logo size="xl" />
        </div>
        <p
          className="font-body text-sm sm:text-base text-zinc-300/90 tracking-wide max-w-xl text-balance"
          data-testid="portal-choice-tagline"
        >
          A private awareness platform for individuals, organizations, and strategic partners.
        </p>

        {/* Waitlist — Founding Members */}
        <div className="mt-10 sm:mt-12 w-full flex justify-center">
          <WaitlistStrip />
        </div>

        {/* Directive */}
        <div className="mt-14 max-w-2xl">
          <div className="text-[11px] uppercase tracking-[0.32em] text-amber-300 mb-4">
            Choose your private access portal
          </div>
          <p className="font-body text-sm sm:text-base text-zinc-400 leading-relaxed">
            Select the presentation prepared for your invited review.
          </p>
        </div>

        {/* Two cards */}
        <div
          className="mt-14 w-full grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6"
          data-testid="portal-cards"
        >
          {/* ANA card — first on mobile via source order */}
          <Link
            to="/ana-access"
            data-testid="portal-card-ana"
            className="group text-left rounded-3xl p-7 sm:p-8 bg-[rgba(6,12,28,0.68)] backdrop-blur-md border border-teal-300/25 shadow-[0_28px_80px_rgba(0,0,0,0.5)] hover:border-teal-300/60 hover:bg-[rgba(9,20,42,0.78)] hover:-translate-y-0.5 transition-all duration-500"
          >
            <div className="flex items-center gap-2 mb-5">
              <span className="w-9 h-9 rounded-xl bg-teal-400/10 border border-teal-300/30 flex items-center justify-center">
                <HeartPulse size={16} className="text-teal-300" />
              </span>
              <span className="text-[11px] uppercase tracking-[0.28em] text-teal-300 font-body">
                Private Concept Presentation
              </span>
            </div>
            <h2 className="font-heading font-medium text-2xl sm:text-[26px] text-white leading-snug tracking-tight mb-3">
              My Mood Groove Nurses Portal
            </h2>
            <p className="font-body text-[14.5px] text-zinc-300/95 leading-relaxed">
              A dedicated concept experience focused on nurse awareness, integration with nurse-led
              programs and resources, privacy, behavioral governance, and a responsible pilot pathway.
            </p>
            <div className="mt-7 inline-flex items-center gap-2 text-teal-200 font-heading font-medium text-sm tracking-tight group-hover:text-white transition">
              Enter the Nurses Portal
              <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
            </div>
          </Link>

          {/* Investor card */}
          <Link
            to="/investor-access"
            data-testid="portal-card-investor"
            className="group text-left rounded-3xl p-7 sm:p-8 bg-[rgba(6,12,28,0.68)] backdrop-blur-md border border-amber-300/25 shadow-[0_28px_80px_rgba(0,0,0,0.5)] hover:border-amber-300/60 hover:bg-[rgba(20,15,9,0.78)] hover:-translate-y-0.5 transition-all duration-500"
          >
            <div className="flex items-center gap-2 mb-5">
              <span className="w-9 h-9 rounded-xl bg-amber-400/10 border border-amber-300/30 flex items-center justify-center">
                <TrendingUp size={16} className="text-amber-300" />
              </span>
              <span className="text-[11px] uppercase tracking-[0.28em] text-amber-300 font-body">
                Private Investor Preview
              </span>
            </div>
            <h2 className="font-heading font-medium text-2xl sm:text-[26px] text-white leading-snug tracking-tight mb-3">
              Investor and Strategic Partner Portal
            </h2>
            <p className="font-body text-[14.5px] text-zinc-300/95 leading-relaxed">
              A private presentation focused on the My Mood Groove vision, business opportunity,
              technology, intellectual property, roadmap, partnership potential, and investment strategy.
            </p>
            <div className="mt-7 inline-flex items-center gap-2 text-amber-200 font-heading font-medium text-sm tracking-tight group-hover:text-white transition">
              Enter Investor Preview
              <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
            </div>
          </Link>
        </div>

        {/* Footer note */}
        <p className="mt-14 text-[11px] text-zinc-500 font-body tracking-wide max-w-xl leading-relaxed">
          Private presentation environments for invited reviewers. Access does not imply
          endorsement, partnership, or investment participation.
        </p>
        <p className="mt-4 text-[10.5px] uppercase tracking-[0.22em] text-amber-200/60 font-body" data-testid="patent-pending-note">
          My Mood Groove · U.S. Patent Pending · Confirmed August 2026
        </p>
      </div>
    </div>
  );
}
